import React, { useCallback, useState, useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { useSpotlight } from '@atlaskit/onboarding';
import { token } from '@atlaskit/tokens';

import { useChoreographerAssets } from '@confluence/choreographer-services';
import { useSetOnboardingState } from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState';
import { fg } from '@confluence/feature-gating';
import { ONBOARDING_SHARE_AND_RESTRICT_DIALOG } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants';

import { SAC_CONTENT_ADD_REMOVE_DOCUMENTATION } from '../../../controller/Controller';
import { useDelay } from '../useDelay';

const SPOTLIGHT_KEY = ONBOARDING_SHARE_AND_RESTRICT_DIALOG.GENERAL_AND_SPECIFIC_ACCESS_SEEN;
const TEMP_ADD_REMOVE_DOCUMENTATION =
	'https://confluence-permissions-help.atlassian.net/wiki/external/MTdiMzM0YTg5OThlNGUyM2I4NTc4NjQ4YmM4YWI4ZDk';

export const i18n = defineMessages({
	done: {
		id: 'share-and-restrict-dialog.header.done-label',
		defaultMessage: 'Done',
		description: 'A label for a spotlight dialog button to end spotlight',
	},
	next: {
		id: 'share-and-restrict-dialog.header.next-label',
		defaultMessage: 'Next',
		description: 'A label for a spotlight dialog button to end spotlight',
	},
	learnMore: {
		id: 'share-and-restrict-dialog.header.learn-more-label',
		defaultMessage: 'Learn more',
		description: 'A label for a spotlight dialog button to learn more about feature',
	},
	generalAccessHeading: {
		id: 'share-and-restrict-dialog.header.general-access-heading',
		defaultMessage: 'Control general access',
		description: 'A heading for general access control spotlight',
	},
	generalAccessDescription: {
		id: 'share-and-restrict-dialog.header.general-access-description',
		defaultMessage:
			'Choose what general audience should have access to this content — anyone in this space or only specific people.',
		description: 'A description for general access control spotlight',
	},
	specificAccessHeading: {
		id: 'share-and-restrict-dialog.header.specific-heading',
		defaultMessage: 'View specific access',
		description: 'A heading to view specific access list spotlight',
	},
	specificAccessDescription: {
		id: 'share-and-restrict-dialog.header.specific-description',
		defaultMessage:
			'Only specific people with greater access than the general access settings will appear here.',
		description: 'A description to view specific access list spotlight',
	},
	generalAccessPermissionDropdownHeading: {
		id: 'share-and-restrict-dialog.header.general-access-permission-dropdown-heading',
		defaultMessage: 'Control general permission',
		description: 'A heading for general permission control spotlight',
	},
	generalAccessPermissionDropdownDescription: {
		id: 'share-and-restrict-dialog.header.general-access-permission-dropdown-description',
		defaultMessage: 'Choose whether the general audience can view or edit.',
		description: 'A description for general permission control spotlight',
	},
});

const TOUR_STEPS: Array<'generalAccess' | 'permissionDropdown' | 'specificAccess'> = [
	'generalAccess',
	'permissionDropdown',
	'specificAccess',
];

export function useHeaderChangeBoarding(isSpotlightActive: boolean) {
	const { setOnboardingState } = useSetOnboardingState();
	const { isTargetRendered } = useSpotlight();
	const [activeSpotlight, setActiveSpotlight] = useState<number | null>(null);
	const next = useCallback(() => setActiveSpotlight((prev) => (prev || 0) + 1), []);
	const { formatMessage } = useIntl();
	const { Spotlight } = useChoreographerAssets();

	const currentTargets = TOUR_STEPS.filter((target) => isTargetRendered(target));
	const isSpecificAccessVisible = currentTargets.some((target) => target === 'specificAccess');
	const isPermissionsDropdownVisible = currentTargets.some(
		(target) => target === 'permissionDropdown',
	);

	// Introducing a delay in showing the spotlights to allow dialog UI to settle and avoid misalignments
	const isDelayOver = useDelay(3000);

	useEffect(() => {
		if (isSpotlightActive && isDelayOver) {
			setActiveSpotlight(0);
		}
	}, [isSpotlightActive, isDelayOver]);

	const handleEnd = useCallback(() => {
		void setOnboardingState({
			key: SPOTLIGHT_KEY,
			value: 'true',
		});
		setActiveSpotlight(null);
	}, [setOnboardingState]);

	const handleLearnMore = useCallback(() => {
		const learMoreLink = fg('confluence_frontend_unified_restrict_and_share_ga')
			? SAC_CONTENT_ADD_REMOVE_DOCUMENTATION
			: TEMP_ADD_REMOVE_DOCUMENTATION;

		window.open(learMoreLink, '_blank');
	}, []);

	const renderActiveSpotlight = useCallback(() => {
		if (activeSpotlight == null) {
			return null;
		}

		const spotlights = [
			{
				target: 'generalAccess',
				spotlight: (
					<Spotlight
						messageType="engagement"
						messageId="generalAccess"
						actions={[
							{
								onClick: next,
								text: formatMessage(i18n.next),
							},
							{ onClick: handleLearnMore, text: formatMessage(i18n.learnMore) },
						]}
						heading={formatMessage(i18n.generalAccessHeading)}
						target="generalAccess"
						key="generalAccess"
						targetRadius={3}
						targetBgColor={token('color.text.inverse')}
						dialogWidth={275}
						actionsBeforeElement={currentTargets.length === 2 ? '1/2' : '1/3'}
					>
						{formatMessage(i18n.generalAccessDescription)}
					</Spotlight>
				),
			},
			{
				target: 'permissionDropdown',
				spotlight: (
					<Spotlight
						messageType="engagement"
						messageId="permissionDropdown"
						actions={[
							isSpecificAccessVisible
								? {
										onClick: next,
										text: formatMessage(i18n.next),
									}
								: { onClick: handleEnd, text: formatMessage(i18n.done) },
							{ onClick: handleLearnMore, text: formatMessage(i18n.learnMore) },
						]}
						heading={formatMessage(i18n.generalAccessPermissionDropdownHeading)}
						target="permissionDropdown"
						key="permissionDropdown"
						targetRadius={3}
						targetBgColor={token('color.text.inverse')}
						dialogWidth={275}
						actionsBeforeElement={isSpecificAccessVisible ? '2/3' : '2/2'}
						dialogPlacement={isSpecificAccessVisible ? 'right top' : 'bottom right'}
					>
						{formatMessage(i18n.generalAccessPermissionDropdownDescription)}
					</Spotlight>
				),
			},
			{
				target: 'specificAccess',
				spotlight: (
					<Spotlight
						messageType="engagement"
						messageId="specificAccess"
						actions={[
							{ onClick: handleEnd, text: formatMessage(i18n.done) },
							{ onClick: handleLearnMore, text: formatMessage(i18n.learnMore) },
						]}
						heading={formatMessage(i18n.specificAccessHeading)}
						target="specificAccess"
						key="specificAccess"
						targetRadius={3}
						targetBgColor={token('color.text.inverse')}
						dialogWidth={275}
						actionsBeforeElement={isPermissionsDropdownVisible ? '3/3' : '2/2'}
						dialogPlacement="right bottom"
					>
						{formatMessage(i18n.specificAccessDescription)}
					</Spotlight>
				),
			},
		]
			.filter(({ target }) => isTargetRendered(target))
			.map(({ spotlight }) => spotlight);

		return spotlights[activeSpotlight];
	}, [
		activeSpotlight,
		currentTargets,
		isPermissionsDropdownVisible,
		isSpecificAccessVisible,
		formatMessage,
		handleLearnMore,
		next,
		handleEnd,
		isTargetRendered,
		Spotlight,
	]);

	return { renderActiveSpotlight };
}
